import { Breadcrumb, Collapse, Divider, Modal, Typography, message, Select } from "antd";
import { RxLoop } from "react-icons/rx";
import { MdOutlineArrowOutward, MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaChevronDown } from "react-icons/fa";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useApiCall } from "../services/api";
import { Link, NavLink, useNavigate, useOutletContext } from "react-router-dom";
import SelectClientModal from "../components/models/SelectClient";
import ChangeAddress from "../components/models/ChangeAddress";
import { useGlobalModal } from "../context/SuccessModalProvider";
import ConfirmStatusModal from "../components/models/ConfirmStatusModal";
import { cartRefresh, triggerRefresh } from "../redux/auth/cartRefreshSlice";
import PrimaryButton from "../components/Forms/PrimaryButton";
import {
  availibilityStatusMap,
  formatAddress,
  getOrderLongestDispatchTime,
  handleRouteNav,
  roundDecimal,
  showAPC,
  showEstimatedDispatchTime,
} from "../utils";
import { disclaimerItems, DYED_TYPES, estimatedDispatchTimes, roleIds } from "../constant";
import { PiBuildingsFill, PiIdentificationCard, PiWalletFill } from "react-icons/pi";
import { setCounts } from "../redux/auth/navCountSlice";
import { badgeVariantStyles, CustomDot } from "../components/Charts/CustomBadge";

const { Title } = Typography;

const CheckOut = () => {
  const { postApi, loading } = useApiCall();
  const [cartItems, setCartItems] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const { full_name, user_id, role_id, customer_id, is_customer, is_sales_manager, is_admin } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [isSelectClientModalOpen, setIsSelectClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [defaultAddress, setDefaultAddress] = useState({
    deliveryAddress: "",
    billingAddress: "",
  });
  const [changeAddressModal, setChangeAddressModal] = useState(false);
  const [modalAddressType, setModalAddressType] = useState("delivery");

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [isOutOfStockModalVisible, setIsOutOfStockModalVisible] = useState(false);
  const [outOfStockUpdatedItems, setOutOfStockUpdatedItems] = useState([]);
  const [cartUpdatingLoader, setCartUpdatingLoader] = useState(false);

  const { showPopup } = useGlobalModal();

  const navigate = useNavigate();

  const fetchCartDetails = async () => {
    try {
      const response = await postApi(
        is_customer ? "cart/get-cart-customer-id" : "sales/cart/get-cart-user-id",
        is_customer ? { customer_id } : { user_id },
      );

      if (response?.status) {
        setCartItems(
          response?.data?.map((item) => ({
            ...item,
            cases_in_stock: Number(item.cases_in_stock || 0),
            cases_in_folding: Number(item.cases_in_folding || 0),
            cases_in_grey: Number(item.cases_in_grey || 0),
            cases_make_to_order: Number(item.cases_make_to_order || 0),
            total_quantity: Number(item.total_quantity),
            total_folding_quantity: Number(item.total_folding_quantity || 0),
            total_in_stock_quantity: Number(item.total_in_stock_quantity || 0),
            total_grey_quantity: Number(item.total_grey_quantity || 0),
            total_make_to_order_quantity: Number(item.total_make_to_order_quantity || 0),
          })),
        );
      } else setCartItems([]);
      dispatch(setCounts({ cartItems: response?.total_items || 0 }));
    } catch (error) {
      console.error("Error fetching cart details:", error);
    }
  };

  const fetchAllClients = async () => {
    try {
      const response = await postApi("sales/assign-customer-list", {
        user_id,
      });
      setAllClients(response.data || []);
    } catch (error) {
      console.error("Error fetching client details:", error);
    }
  };

  // once the address flow is complete, do this
  const fetchAddresses = async (customer) => {
    if (!customer?.customer_id) return;
    try {
      const urlEndPoint = is_customer ? "address" : "sales/address/list";
      const response = await postApi(urlEndPoint, {
        customer_id: customer.customer_id,
      });
      setAddresses(response.data || []);

      const defaultDeliveryAddr = response.data?.find((addr) => addr.is_default_delivery) || "";
      const defaultBillingAddr = response.data?.find((addr) => addr.is_default_billing) || "";

      if (defaultDeliveryAddr) {
        setDefaultAddress((prev) => ({
          ...prev,
          deliveryAddress: defaultDeliveryAddr,
        }));
      } else {
        const { address_line1, address_line2, city, state, post_code, country } = customer;
        setDefaultAddress((prev) => ({
          ...prev,
          deliveryAddress: {
            address_line1,
            address_line2,
            city,
            state,
            post_code,
            country,
          },
        }));
      }
      if (defaultBillingAddr) {
        setDefaultAddress((prev) => ({
          ...prev,
          billingAddress: defaultBillingAddr,
        }));
      } else {
        const { address_line1, address_line2, city, state, post_code, country } = customer;
        setDefaultAddress((prev) => ({
          ...prev,
          billingAddress: {
            address_line1,
            address_line2,
            city,
            state,
            post_code,
            country,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const handleItemDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedItem(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;

    try {
      const response = await postApi(is_customer ? "cart/delete-item" : "sales/cart/delete-item", { cart_id: selectedItem.cart_id }, true, null, () =>
        dispatch(triggerRefresh()),
      );
      if (response?.status) {
        fetchCartDetails();
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
    } finally {
      setIsDeleteModalVisible(false);
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    fetchCartDetails();

    if (is_customer) {
      fetchAddresses({ customer_id: customer_id });
      setSelectedClient({ customer_id: customer_id });
    }
  }, []);

  const handleSubmit = async () => {
    const deliveryAddressString = formatAddress(defaultAddress?.deliveryAddress) || formatAddress(selectedClient);
    const billingAddressString = formatAddress(defaultAddress?.billingAddress) || formatAddress(selectedClient);

    const inStockItems = cartItems
      .filter((i) => Number(i.total_in_stock_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 1,
        in_stock_folding: 0,
        in_stock_grey: 0,
        folding_barcode: "",
        folding_id: "",
        grey_id: "",
        grey_pcsno: "",
        quantity_meters: i.total_in_stock_quantity,
        cases: i.cases_in_stock,
      }));

    const foldingItems = cartItems
      .filter((i) => Number(i.total_folding_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 0,
        in_stock_folding: 1,
        in_stock_grey: 0,
        cases_id: "",
        cases_no: "",
        grey_id: "",
        grey_pcsno: "",
        quantity_meters: i.total_folding_quantity,
        cases: i.cases_in_folding,
      }));

    const greyItems = cartItems
      .filter((i) => Number(i.total_grey_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 0,
        in_stock_folding: 0,
        in_stock_grey: 1,
        cases_id: "",
        cases_no: "",
        folding_barcode: "",
        folding_id: "",
        quantity_meters: i.total_grey_quantity,
        cases: i.cases_in_grey,
      }));

    const makeToOrderItems = cartItems
      .filter((i) => Number(i.total_make_to_order_quantity) ?? 0 > 0)
      .map((i) => ({
        ...i,
        in_stock: 0,
        in_stock_folding: 0,
        in_stock_grey: 0,
        cases_id: "",
        cases_no: "",
        folding_barcode: "",
        folding_id: "",
        grey_id: "",
        grey_pcsno: "",
        quantity_meters: i.total_make_to_order_quantity,
        cases: i.cases_make_to_order,
      }));

    const finalItems = [...inStockItems, ...foldingItems, ...greyItems, ...makeToOrderItems].map((i) => {
      delete i.updated_by;
      delete i.updated_by_id;
      return {
        ...i,
      };
    });

    const body = {
      customer_id: selectedClient?.customer_id,
      delivery_address: deliveryAddressString,
      delivery_title: defaultAddress?.deliveryAddress?.title || "Home",
      billing_address: billingAddressString,
      billing_title: defaultAddress?.billingAddress?.title || "Home",
      user_id: is_customer ? 0 : user_id,
      assigned_user_id: is_sales_manager || is_admin ? selectedClient.user_id : user_id,
      order_items: finalItems,
      updated_by: full_name,
      updated_by_id: is_customer ? 0 : user_id,
      delivery_city: defaultAddress?.deliveryAddress?.city || selectedClient?.city,
      sales_name: selectedClient?.user?.full_name || "",
      party_code: selectedClient?.party_code,
    };

    try {
      const response = await postApi("order/add-order", body, true);

      if (response?.status) {
        fetchCartDetails();
        setSelectedClient(is_customer ? customer_id : null);
        dispatch(triggerRefresh());
      } else if (response?.data || response?.data_folding || response?.data_grey) {
        const bsrMap = new Map((response.data || []).map((item) => [String(item.cart_id), item]));
        const foldingMap = new Map((response.data_folding || []).map((item) => [String(item.cart_id), item]));
        const greyMap = new Map((response.data_grey || []).map((item) => [String(item.cart_id), item]));

        const filterIdentifiers = (originalIds, originalNos, validNos) => {
          if (!originalNos || !validNos) return { ids: "", nos: "" };
          const validSet = new Set(
            String(validNos)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          );
          const idsArr = String(originalIds || "").split(",");
          const nosArr = String(originalNos).split(",");
          const updatedIds = [];
          const updatedNos = [];

          nosArr.forEach((no, index) => {
            const trimmedNo = no.trim();
            if (validSet.has(trimmedNo)) {
              updatedNos.push(trimmedNo);
              if (idsArr[index]) {
                updatedIds.push(idsArr[index].trim());
              }
            }
          });
          return { ids: updatedIds.join(","), nos: updatedNos.join(",") };
        };

        const resolveIdentifiers = (updateObj, validStr, originalIds, originalNos, newCasesCount) => {
          if (updateObj && validStr) {
            return filterIdentifiers(originalIds, originalNos, validStr);
          }
          if (updateObj && newCasesCount > 0) {
            return {
              ids: String(originalIds || "")
                .split(",")
                .slice(0, newCasesCount)
                .join(","),
              nos: String(originalNos || "")
                .split(",")
                .slice(0, newCasesCount)
                .join(","),
            };
          }
          if (newCasesCount === 0) {
            return { ids: "", nos: "" };
          }
          return { ids: originalIds, nos: originalNos };
        };

        const updatedItemsWithCart = cartItems
          .map((cartItem) => {
            const SLAB_MTRS = Number(cartItem.slab_mtrs) || 250;
            const cartId = String(cartItem.cart_id);

            const bsrUpdate = bsrMap.get(cartId);
            const foldingUpdate = foldingMap.get(cartId);
            const greyUpdate = greyMap.get(cartId);

            if (!bsrUpdate && !foldingUpdate && !greyUpdate) return null;

            let hasChanges = false;

            const oldBsrCases = Number(cartItem.cases_in_stock || 0);
            const oldBsrQty = roundDecimal(Number(cartItem.total_in_stock_quantity || 0));
            const oldFoldingCases = Number(cartItem.cases_in_folding || 0);
            const oldFoldingQty = roundDecimal(Number(cartItem.total_folding_quantity || 0));
            const oldGreyCases = Number(cartItem.cases_in_grey || 0);
            const oldGreyQty = roundDecimal(Number(cartItem.total_grey_quantity || 0));
            const oldMtoCases = Number(cartItem.cases_make_to_order || 0);
            const oldMtoQty = roundDecimal(Number(cartItem.total_make_to_order_quantity || 0));

            let newBsrCases = oldBsrCases,
              newBsrQty = oldBsrQty;
            let newFoldingCases = oldFoldingCases,
              newFoldingQty = oldFoldingQty;
            let newGreyCases = oldGreyCases,
              newGreyQty = oldGreyQty;

            if (bsrUpdate) {
              newBsrQty = roundDecimal(Number(bsrUpdate.total_qty || 0));
              newBsrCases = bsrUpdate.cases ? bsrUpdate.cases.split(",").filter(Boolean).length : 0;
              const diffBsrQty = Math.abs(newBsrQty - oldBsrQty);
              if (oldBsrCases !== newBsrCases || diffBsrQty > 0.1) hasChanges = true;
            }

            if (foldingUpdate) {
              newFoldingQty = roundDecimal(Number(foldingUpdate.total_qty || 0));
              newFoldingCases = newFoldingQty > 0 ? Math.ceil(newFoldingQty / SLAB_MTRS) : 0;
              const diffFoldingQty = Math.abs(newFoldingQty - oldFoldingQty);
              if (oldFoldingCases !== newFoldingCases || diffFoldingQty > 0.1) hasChanges = true;
            }

            if (greyUpdate) {
              const rawGreyQty = Number(greyUpdate.total_qty || 0);
              newGreyQty = roundDecimal(rawGreyQty * 0.95);
              newGreyCases = newGreyQty > 0 ? Math.ceil(newGreyQty / SLAB_MTRS) : 0;
              const diffGreyQty = Math.abs(newGreyQty - oldGreyQty);
              if (oldGreyCases !== newGreyCases || diffGreyQty > 0.1) hasChanges = true;
            }

            if (!hasChanges) return null;

            const diffBsrCases = Math.max(0, oldBsrCases - newBsrCases);
            const diffFoldingCases = Math.max(0, oldFoldingCases - newFoldingCases);
            const diffGreyCases = Math.max(0, oldGreyCases - newGreyCases);

            const newMtoCases = oldMtoCases + diffBsrCases + diffFoldingCases + diffGreyCases;
            const newMtoQty = roundDecimal(oldMtoQty + (diffBsrCases + diffFoldingCases + diffGreyCases) * SLAB_MTRS);

            const { ids: finalBsrIds, nos: finalBsrNos } = resolveIdentifiers(
              bsrUpdate,
              bsrUpdate?.cases,
              cartItem.cases_id,
              cartItem.cases_no,
              newBsrCases,
            );

            const { ids: finalFoldingIds, nos: finalFoldingBarcodes } = resolveIdentifiers(
              foldingUpdate,
              foldingUpdate?.bar_code || foldingUpdate?.folding_barcode || foldingUpdate?.cases,
              cartItem.folding_id,
              cartItem.folding_barcode,
              newFoldingCases,
            );

            const { ids: finalGreyIds, nos: finalGreyPcsnos } = resolveIdentifiers(
              greyUpdate,
              greyUpdate?.pcsno || greyUpdate?.grey_pcsno || greyUpdate?.cases,
              cartItem.grey_id,
              cartItem.grey_pcsno,
              newGreyCases,
            );

            return {
              ...cartItem,

              old_cases_in_stock: oldBsrCases,
              old_total_in_stock_quantity: oldBsrQty,
              old_cases_in_folding: oldFoldingCases,
              old_total_folding_quantity: oldFoldingQty,
              old_cases_in_grey: oldGreyCases,
              old_total_grey_quantity: oldGreyQty,
              old_cases_make_to_order: oldMtoCases,
              old_total_make_to_order_quantity: oldMtoQty,

              old_cases_id: cartItem.cases_id,
              old_folding_id: cartItem.folding_id,
              old_grey_id: cartItem.grey_id,

              cases_in_stock: newBsrCases,
              cases_in_folding: newFoldingCases,
              cases_in_grey: newGreyCases,
              cases_make_to_order: newMtoCases,

              total_in_stock_quantity: newBsrQty,
              total_folding_quantity: newFoldingQty,
              total_grey_quantity: newGreyQty,
              total_make_to_order_quantity: newMtoQty,

              cases_id: finalBsrIds,
              cases_no: finalBsrNos,
              folding_id: finalFoldingIds,
              folding_barcode: finalFoldingBarcodes,
              grey_id: finalGreyIds,
              grey_pcsno: finalGreyPcsnos,
            };
          })
          .filter(Boolean);

        if (updatedItemsWithCart.length > 0) {
          setOutOfStockUpdatedItems(updatedItemsWithCart);
          setIsOutOfStockModalVisible(true);
        } else {
          showPopup({
            title: "Order Failed",
            content: (
              <span className="custom-modal-title">Some items in your cart are no longer available. Please clear your cart and try again.</span>
            ),
            className: "custom-success-modal",
          });
        }
      }
    } catch (error) {
      console.log("Error submitting form:", error);
    }
  };

  const handleItemEdit = (item) => {
    navigate(handleRouteNav(`/product-details/${item.sort_no}`, role_id), {
      state: { cart_id: item.cart_id, cart_item_tdlevel_code: item.tdlevel_code },
    });
  };

  const handleUpdateCartItems = async () => {
    try {
      setCartUpdatingLoader(true);
      const updateUrl = is_customer ? "cart/update-cart" : "sales/cart/update-cart";

      for (const cartItem of outOfStockUpdatedItems) {
        const payload = {
          ...cartItem,
          total_quantity: Number(cartItem.total_quantity || 0),
        };

        const res = await postApi(updateUrl, payload);
        if (!res?.status) {
          console.error(`Failed to update cart ${cartItem.cart_id}`);
        }
      }

      setIsOutOfStockModalVisible(false);
      fetchCartDetails();
      showPopup({
        title: null,
        content: <span className="custom-modal-title">Cart items updated.</span>,
        onOk: () => console.log("Confirmed!"),
        className: "custom-success-modal",
      });
    } catch (error) {
      console.error("Error updating cart items:", error);
    } finally {
      setCartUpdatingLoader(false);
    }
  };

  const handleCheckoutAddressSaved = (savedAddress) => {
    setDefaultAddress((prev) => {
      const next = { ...prev };

      if (prev?.deliveryAddress?.address_id === savedAddress?.address_id) {
        next.deliveryAddress = { ...prev.deliveryAddress, ...savedAddress };
      }

      if (prev?.billingAddress?.address_id === savedAddress?.address_id) {
        next.billingAddress = { ...prev.billingAddress, ...savedAddress };
      }

      return next;
    });
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Checkout");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Checkout",
              href: handleRouteNav("/product-catalog/checkout", role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  return (
    <>
      <>
        <div className={is_customer ? "flex flex-1 justify-between pb-6" : "flex justify-between"}>
          <div className="flex flex-1 max-w-[55%] w-[45%] flex-col gap-4 pb-6">
            {!is_customer && (
              <div className="bg-[#ffffff] w-[100%] p-6 flex flex-col gap-2 rounded-3xl">
                <div className="flex items-center justify-between gap-4 leading-8">
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                      <img src={"/assets/icons/User.png"} alt="card-logo" className="w-6 h-6" />
                    </div>
                    <p className="text-[#000000] font-semibold text-lg truncate min-w-0">Client name</p>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center justify-end flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSelectClientModalOpen(true);
                      }}
                      className="flex items-center gap-2 text-[#A33434] cursor-pointer px-3 py-2 hover:bg-transparent min-w-0"
                    >
                      <span className="truncate max-w-[150px] block">{selectedClient?.company_name || "Select Client"}</span>
                      <svg
                        className="w-4 h-4 text-[#A33434]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="border-2 rounded-2xl p-4 mt-2">
                  <p className="text-[#000] text-left font-bold text-base leading-8">{selectedClient?.company_name || "Select Client"}</p>
                </div>
              </div>
            )}
            {selectedClient && (
              <>
                <div className="bg-[#ffffff] w-[100%] p-6 flex flex-col gap-2 rounded-3xl">
                  <div className="flex flex-wrap gap-2 leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <PiBuildingsFill size={24} fill="#A33434" />
                      </div>
                      <p className="text-[#000000] font-semibold text-lg leading-3">Deliver to</p>
                    </div>
                    <PrimaryButton
                      text="Change Address"
                      icon={<RxLoop color="#fff" />}
                      onClick={() => {
                        setChangeAddressModal(true);
                        setModalAddressType("delivery");
                      }}
                      iconOnleft
                      // textClassName="text-[#A33434] font-medium text-base leading-3 "
                      // iconClassName="!h-[30px] !w-[30px]"
                      btnType="ghost"
                    />
                  </div>
                  <div className="border-2 rounded-2xl p-4 mt-2">
                    <p className="text-[#000] text-left text-base leading-6 flex flex-col gap-2">
                      <strong className="text-base">{defaultAddress?.deliveryAddress?.title}</strong>
                      {formatAddress(defaultAddress?.deliveryAddress) || formatAddress(selectedClient)}
                    </p>
                  </div>
                </div>
                <div className="bg-[#ffffff] w-[100%] p-6 flex flex-wrap flex-col gap-2 rounded-3xl">
                  <div className="flex flex-wrap gap-2 leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[45px] h-[45px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <PiWalletFill size={24} fill="#A33434" />
                      </div>
                      <p className="text-[#000000] font-semibold text-lg leading-3">Bill to</p>
                    </div>
                    <PrimaryButton
                      text="Change Address"
                      icon={<RxLoop color="#fff" />}
                      onClick={() => {
                        setChangeAddressModal(true);
                        setModalAddressType("billing");
                      }}
                      iconOnleft
                      // textClassName="text-[#A33434] font-medium text-base leading-3 "
                      // iconClassName="!h-[30px] !w-[30px]"
                      btnType="ghost"
                    />
                  </div>
                  <div className="border-2 rounded-2xl p-4 mt-2">
                    <p className="text-[#000] text-left text-base leading-6 flex flex-col gap-2">
                      <strong className="text-base">{defaultAddress?.billingAddress?.title}</strong>
                      {formatAddress(defaultAddress?.billingAddress) || formatAddress(selectedClient)}
                    </p>
                  </div>
                </div>
                <div className="bg-[#ffffff] w-[100%] px-6 py-4 flex flex-col gap-2 rounded-3xl">
                  <div className="flex leading-8 justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-[30px] h-[30px] min-w-[30px] min-h-[30px] bg-[#A334341A] flex justify-center items-center rounded-full">
                        <img src={"/assets/icons/TruckTrailer.png"} alt="card-logo" className="w-[20px] h-[20px]" />
                      </div>
                      <p className="text-[#000000] leading-5 text-start">
                        <strong>Estimated Dispatch</strong> - {getOrderLongestDispatchTime(cartItems)}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div>
              <PrimaryButton
                className="mx-auto"
                disabled={!selectedClient || !cartItems.length}
                text="Place Order"
                onClick={handleSubmit}
                loading={loading}
              />
            </div>

            <div className=" pt-6 rounded-lg mt-8">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Disclaimers</h2>
              </div>

              <div className="space-y-4">
                {disclaimerItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1.5">
                      <div className="w-2 h-2 bg-[#A33434] rounded-full"></div>
                    </div>
                    <p className="flex-1 text-left text-gray-600 leading-relaxed text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Divider type="vertical" className="mx-6 border-l-2 border-gray-300 self-stretch h-auto mb-[10%]" />

          <div className="flex max-w-[400px] w-[45%] flex-col gap-4 pb-6">
            {cartItems.length > 0 ? (
              cartItems.map((item, index) => {
                return (
                  <div className="bg-[#ffffff] w-[100%] px-4 py-2 rounded-3xl" key={item.cart_id + "-" + index}>
                    <Collapse
                      defaultActiveKey={["1"]}
                      ghost
                      expandIconPosition="end"
                      expandIcon={({ isActive }) => (
                        <FaChevronDown
                          className="transition-transform duration-300 mt-2"
                          style={{
                            transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      )}
                      items={[
                        {
                          key: "1",
                          label: (
                            <div className="w-full rounded-2xl">
                              <div className="flex items-center gap-4">
                                <div className="w-[50px] h-[50px] bg-[#A334341A] flex justify-center items-center rounded-full">
                                  <img src={item.product_picture_url} alt={item.short_name} className="w-[50px] h-[50px]" />
                                </div>
                                <div className="flex flex-col gap-1 justify-start text-start">
                                  <p className="text-xl font-bold break-words whitespace-normal">{item.short_name}</p>
                                  <p className="font-semibold text-gray-500 text-sm">{item.tdlevel_name}</p>
                                  {/* <p className="font-semibold text-gray-500 text-sm">
                                    ₹{Number(item.price_per_meter || 0).toLocaleString("en-IN")} /
                                    meter
                                  </p> */}
                                </div>
                              </div>
                            </div>
                          ),
                          children: (
                            <>
                              <div className="w-full rounded-2xl">
                                {/* Details Grid */}
                                <div className="grid grid-cols-2 font-medium leading-6 gap-4 mb-4">
                                  <div className="text-left space-y-1">
                                    <p className="text-sm text-gray-500">Dyed Type</p>
                                    <p className="text-base leading-6 font-medium text-gray-900 capitalize">{item.dye_type}</p>
                                  </div>
                                  <div className="space-y-1 text-right">
                                    <p className="text-sm text-gray-500">Shade</p>
                                    <p className="text-base leading-6 font-medium text-gray-900">
                                      {item.shade_name}
                                      {showAPC(item.apc_no)}
                                    </p>
                                  </div>
                                  {Number(item.cases_in_stock + item.cases_in_folding) > 0 && (
                                    <div className="text-left leading-6 space-y-1">
                                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                                        {availibilityStatusMap.IN_STOCK.text}
                                        <CustomDot color={badgeVariantStyles.success.dotColor} />
                                      </div>
                                      <p className="text-base leading-6 font-medium text-gray-900">
                                        {item.cases_in_stock + item.cases_in_folding || 0} cases (
                                        {item.total_in_stock_quantity + item.total_folding_quantity || 0} m)
                                      </p>
                                    </div>
                                  )}
                                  {Number(item.cases_in_grey) > 0 && (
                                    <div className="text-left leading-6 space-y-1">
                                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                                        {availibilityStatusMap.GREY.text.split(" ")[0]}
                                        <CustomDot color={badgeVariantStyles.greyStock.dotColor} />
                                      </div>
                                      <p className="text-base leading-6 font-medium text-gray-900">
                                        {item.cases_in_grey || 0} cases ({item.total_grey_quantity || 0} m)
                                      </p>
                                    </div>
                                  )}
                                  {Number(item.cases_make_to_order) > 0 && (
                                    <div className={`${Number(item.cases_in_stock) > 0 ? "text-right" : "text-left"} leading-6 space-y-1`}>
                                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                                        {availibilityStatusMap.MTO.text}
                                        <CustomDot color={badgeVariantStyles.makeToOrder.dotColor} />
                                      </div>
                                      <p className="text-base leading-6 font-medium text-gray-900">
                                        {item.cases_make_to_order || 0} cases ({item.total_make_to_order_quantity || 0} m)
                                      </p>
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center gap-3 mb-4">
                                  <div className="w-[30px] h-[30px] min-w-[30px] min-h-[30px] bg-[#A334341A] flex justify-center items-center rounded-full">
                                    <img src={"/assets/icons/TruckTrailer.png"} alt="card-logo" className="w-[20px] h-[20px]" />
                                  </div>
                                  <p className="text-[#000000] leading-5 text-start">
                                    <strong>Estimated Dispatch</strong> -{" "}
                                    {showEstimatedDispatchTime(item, {
                                      showLabel: true,
                                    })}
                                  </p>
                                </div>

                                {/* Comments Section */}
                                <div className=" text-left font-medium leading-6 mb-6 space-y-1">
                                  <p className="text-sm leading-6 text-gray-500">Comments</p>
                                  <p className="text-base leading-6 text-gray-900">{item.comments || "-"}</p>
                                </div>
                                <div>
                                  <Divider className="my-6" />
                                </div>
                                <div className="flex justify-center items-center">
                                  <PrimaryButton
                                    text="Edit"
                                    icon={<MdOutlineEdit color="#fff" />}
                                    onClick={() => handleItemEdit(item)}
                                    iconOnleft
                                    btnType="ghost"
                                  />
                                  <Divider type="vertical" className="mx-6 h-12" />
                                  <PrimaryButton
                                    text="Delete"
                                    icon={<RiDeleteBin6Line color="#fff" />}
                                    onClick={() => handleItemDelete(item)}
                                    iconOnleft
                                    btnType="ghost"
                                  />
                                </div>
                              </div>
                            </>
                          ),
                        },
                      ]}
                    />
                  </div>
                );
              })
            ) : (
              <>
                {is_customer && (
                  <div className="flex flex-col justify-center gap-10">
                    <div className="flex justify-center">
                      <img src="/assets/icons/emptyCart.svg" className="w-fit "></img>
                    </div>
                    <div>
                      <span className="text-[#000] text-md font-bold">Your cart is empty!</span>
                      <div>
                        <span className="text-[#000] text-md">Looks like you haven't made a choice yet.</span>
                      </div>
                    </div>

                    <div>
                      <NavLink to="/product-catalog" className="underline text-[#a33434] text-xl">
                        Go to Catalogue
                      </NavLink>
                    </div>
                  </div>
                )}

                {!is_customer && (
                  <div className="bg-[#fff] p-6 rounded-[32px] text-center">
                    <p className="text-2xl font-bold text-[#A33434]">No items in cart</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
      <SelectClientModal
        isOpen={isSelectClientModalOpen}
        setIsOpen={setIsSelectClientModalOpen}
        title={"Select Client"}
        clients={allClients}
        selectedClient={selectedClient}
        onSubmit={(client) => {
          setSelectedClient(client);
          setIsSelectClientModalOpen(false);
          fetchAddresses(client);
        }}
        loading={loading}
      />

      <ChangeAddress
        isMainModalOpen={changeAddressModal}
        setIsMainModalOpen={setChangeAddressModal}
        addresses={addresses}
        setAddresses={setAddresses}
        customer_id={selectedClient?.customer_id}
        addressType={modalAddressType}
        defaultAddress={defaultAddress}
        setDefaultAddress={setDefaultAddress}
        onAddressSaved={handleCheckoutAddressSaved}
      />
      <ConfirmStatusModal
        open={isDeleteModalVisible}
        onClose={handleCancelDelete}
        handleSubmit={handleConfirmDelete}
        title="Delete Item"
        subTitle="Do you want to delete this item?"
        buttonTitle="Delete Item"
        loading={loading}
      />

      <Modal
        className="flex flex-col items-center justify-center form-css-5 full-width-modal"
        open={isOutOfStockModalVisible}
        footer={null}
        onCancel={() => setIsOutOfStockModalVisible(false)}
        width={600}
      >
        <Title className="input-css font-abhaya" level={2}>
          Important Information!
        </Title>
        <span className=" text-gray-500">The following items were automatically updated because some quantities were not found in stock.</span>

        <div className="w-full mt-5">
          {outOfStockUpdatedItems.length > 0 ? (
            outOfStockUpdatedItems.map((item, idx) => {
              return (
                <div key={idx} className="w-full rounded-2xl mt-5 border p-3">
                  <div className="flex items-center gap-4">
                    <div className="w-[50px] h-[50px] bg-[#A334341A] flex justify-center items-center rounded-full">
                      <img src={item.product_picture_url} alt={item.short_name} className="w-[50px] h-[50px]" />
                    </div>
                    <div className="flex flex-col justify-start text-start">
                      <p className="text-xl font-bold break-words whitespace-normal">{item.short_name || `Cart ${item.cart_id}`}</p>
                      <p className="font-semibold text-gray-500 text-lg">
                        {item.shade_name}
                        {showAPC(item.apc_no)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
                    <div className="text-left leading-6 space-y-1">
                      {item.old_cases_id || item.old_folding_id ? (
                        <>
                          <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                            {availibilityStatusMap.IN_STOCK.text} (OLD)
                            <CustomDot color={badgeVariantStyles.success.dotColor} />
                          </div>

                          <p className="text-base leading-6 font-medium text-gray-900">
                            {item.old_cases_in_stock + item.old_cases_in_folding || 0} cases (
                            {item.old_total_in_stock_quantity + item.old_total_folding_quantity || 0} m)
                          </p>
                        </>
                      ) : item.old_grey_id ? (
                        <>
                          <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                            {availibilityStatusMap.GREY.text.split(" ")[0]} (OLD)
                            <CustomDot color={badgeVariantStyles.greyStock.dotColor} />
                          </div>

                          <p className="text-base leading-6 font-medium text-gray-900">
                            {item.old_cases_in_grey || 0} cases ({item.old_total_grey_quantity || 0} m)
                          </p>
                        </>
                      ) : null}
                    </div>
                    <div className="text-right leading-6 space-y-1">
                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                        {availibilityStatusMap.MTO.text}
                        <CustomDot color={badgeVariantStyles.makeToOrder.dotColor} />
                      </div>
                      <p className="text-base leading-6 font-medium text-gray-900">
                        {item.old_cases_make_to_order || 0} cases ({item.old_total_make_to_order_quantity || 0} m)
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 font-medium leading-6 gap-4 my-3">
                    <div className="text-left leading-6 space-y-1">
                      {item.old_cases_id || item.old_folding_id ? (
                        <>
                          <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                            {availibilityStatusMap.IN_STOCK.text} (NEW)
                            <CustomDot color={badgeVariantStyles.success.dotColor} />
                          </div>
                          <p className="text-base leading-6 font-medium text-gray-900">
                            {item.cases_in_stock + item.cases_in_folding || 0} cases (
                            {item.total_in_stock_quantity + item.total_folding_quantity || 0} m)
                          </p>
                        </>
                      ) : item.old_grey_id ? (
                        <>
                          <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                            {availibilityStatusMap.GREY.text.split(" ")[0]} (NEW)
                            <CustomDot color={badgeVariantStyles.greyStock.dotColor} />
                          </div>
                          <p className="text-base leading-6 font-medium text-gray-900">
                            {item.cases_in_grey || 0} cases ({item.total_grey_quantity || 0} m)
                          </p>
                        </>
                      ) : null}
                    </div>
                    <div className="text-right leading-6 space-y-1">
                      <div className="inline-flex items-center text-sm text-gray-500 leading-6 font-medium gap-2">
                        {availibilityStatusMap.MTO.text}
                        <CustomDot color={badgeVariantStyles.makeToOrder.dotColor} />
                      </div>
                      <p className="text-base leading-6 font-medium text-gray-900">
                        {item.cases_make_to_order || 0} cases ({item.total_make_to_order_quantity || 0} m)
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-600 text-center">No updated items</p>
          )}
        </div>
        <div className="flex gap-3 mt-6 w-full justify-center">
          <PrimaryButton
            text="Update cart"
            btnType="primary"
            onClick={handleUpdateCartItems}
            loading={loading || cartUpdatingLoader}
            className="me-[30px]"
          />
        </div>
      </Modal>
    </>
  );
};

export default CheckOut;
